import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetCycles, apiGetMentals, apiGetSports, apiGetDashboardSummary, apiGetPendingInvitations, apiGetStreak, apiRespondToInvitation } from '../services/api';
import StreakBanner from '../components/StreakBanner';
import PageHeader from '../components/PageHeader';
import PeriodSelector from '../components/PeriodSelector';
import { filterWindow, periodWindow, windowDays } from '../utils/dateRange';
import PeriodPager, { usePeriodOffset } from '../components/PeriodPager';
import { ReactComponent as FormIcon } from '../assets/home/form_icon.svg';
import { ReactComponent as ShareIcon } from '../assets/home/share_icon.svg';
import { ReactComponent as OvulationIcon } from '../assets/home/ovulation_icon.svg';
import { ReactComponent as ReglesIcon } from '../assets/cycle/regles_icon.svg';
import { ReactComponent as CycleIcon } from '../assets/cycle/cycle_icon.svg';
import { ReactComponent as MentalIcon } from '../assets/home/mental_icon.svg';
import { ReactComponent as ActivityIcon } from '../assets/common/activity_icon.svg';
import { ReactComponent as EnergieIcon } from '../assets/home/energie_icon.svg';
import { ReactComponent as ConcentrationIcon } from '../assets/home/concentration_icon.svg';
import { ReactComponent as EstimeIcon } from '../assets/home/estime_icon.svg';
import { ReactComponent as InfoFillIcon } from '../assets/home/Info_fill.svg';
import InfoButton from '../components/InfoButton';
import ErrorBanner from '../components/ErrorBanner';
import { PHASE, MENTAL, SPORT, CYCLE_PIE_COLORS, MENTAL_PIE_COLORS, SPORT_PIE_COLORS, HOME, UI, PHASE_KEY_FROM_LABEL } from '../constants/colors';
import { rememberPhase, recallPhase } from '../utils/lastKnownPhase';
import { tipOfTheDay } from '../constants/phaseTips';
import './Home.css';
import { toISODate, todayISO } from '../utils/formDate';
import { MOOD_EMOJI } from '../constants/labels';

// ── Concentric rings chart (pure SVG — fully responsive) ────────────────────
const EMPTY_SLICE = [{ value: 1, fill: HOME.emptySlice }];

function toSlices(slices) {
  if (!slices || slices.every(s => s.pct === 0)) return EMPTY_SLICE;
  return slices.map(s => ({ value: s.pct, fill: s.color, label: s.label }));
}

function arcPath(cx, cy, r1, r2, startDeg, endDeg) {
  if (endDeg - startDeg >= 360) {
    const mid = startDeg + 180;
    return arcPath(cx, cy, r1, r2, startDeg, mid) + ' ' + arcPath(cx, cy, r1, r2, mid, endDeg - 0.001);
  }
  const toRad = d => ((d - 90) * Math.PI) / 180;
  const s = toRad(startDeg), e = toRad(endDeg);
  const x1 = cx + r2 * Math.cos(s), y1 = cy + r2 * Math.sin(s);
  const x2 = cx + r2 * Math.cos(e), y2 = cy + r2 * Math.sin(e);
  const x3 = cx + r1 * Math.cos(e), y3 = cy + r1 * Math.sin(e);
  const x4 = cx + r1 * Math.cos(s), y4 = cy + r1 * Math.sin(s);
  const large = (endDeg - startDeg) > 180 ? 1 : 0;
  return `M${x1.toFixed(2)},${y1.toFixed(2)} A${r2},${r2} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} L${x3.toFixed(2)},${y3.toFixed(2)} A${r1},${r1} 0 ${large},0 ${x4.toFixed(2)},${y4.toFixed(2)} Z`;
}

const ConcentricPie = ({ cycleSlices, cycleSlicesRaw, mentalSlices, sportSlices, cycleDayPct, cycleDay = null, cycleDays = 28, isEstimated = false, size = 240, onSliceClick }) => {
  const cx = size / 2, cy = size / 2, R = size / 2;
  const svgRef = useRef(null);
  const [localPct, setLocalPct] = useState(cycleDayPct ?? 0);
  const [dragging, setDragging] = useState(false);
  const [activeSlice, setActiveSlice] = useState(null); // { ri, i } — hover or touch-active

  useEffect(() => {
    if (!dragging && cycleDayPct != null) setLocalPct(cycleDayPct);
  }, [cycleDayPct, dragging]);

  const hasNeedle = cycleDayPct != null;
  const activePct = hasNeedle ? localPct : null;
  // Au repos, le jour vient de l'API : l'aiguille plafonne à un tour, le jour non
  // (une sportive en retard est à J37, pas à J28). On ne le déduit de l'aiguille
  // que pendant qu'on la fait glisser.
  const displayDay = activePct == null
    ? null
    : !dragging && cycleDay != null
      ? cycleDay
      : Math.max(1, Math.min(cycleDays, Math.ceil(activePct * cycleDays) || 1));

  const phaseAtNeedle = useMemo(() => {
    if (activePct == null || !cycleSlicesRaw?.length) return null;
    const total = cycleSlicesRaw.reduce((s, d) => s + d.pct, 0);
    if (!total) return null;
    let acc = 0;
    for (const s of cycleSlicesRaw) {
      acc += s.pct / total;
      if (activePct <= acc) return { label: s.label, color: s.color };
    }
    return { label: cycleSlicesRaw.at(-1).label, color: cycleSlicesRaw.at(-1).color };
  }, [activePct, cycleSlicesRaw]);

  const RINGS = [
    { slices: toSlices(cycleSlices),  innerPct: 0.28, outerPct: 0.38, key: 'cycle' },
    { slices: toSlices(mentalSlices), innerPct: 0.42, outerPct: 0.52, key: 'mental' },
    { slices: toSlices(sportSlices),  innerPct: 0.56, outerPct: 0.66, key: 'sport' },
  ];

  const pointerState = useRef({ startX: 0, startY: 0, startT: 0, moved: false, active: false, grabbed: false });
  const DRAG_THRESHOLD_PX = 6;
  const TAP_MAX_MS = 250;
  // Au doigt, seul un geste parti de l'aiguille la déplace. Le donut occupe près de la
  // moitié de l'écran d'un téléphone : en capturant tout geste, il empêchait de faire
  // défiler la page dès qu'on posait le doigt dessus.
  const NEEDLE_GRAB_DEG = 22;
  const pctRef = useRef(activePct);
  pctRef.current = activePct;

  const getCoords = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width * size - cx;
    const y = (e.clientY - rect.top) / rect.height * size - cy;
    return { x, y, clientX: e.clientX, clientY: e.clientY };
  };

  const pctFromCoords = ({ x, y }) => {
    let angle = Math.atan2(y, x) * 180 / Math.PI + 90;
    if (angle < 0) angle += 360;
    return angle / 360;
  };

  const hitTestSlice = (coords) => {
    const distance = Math.sqrt(coords.x * coords.x + coords.y * coords.y);
    const angleDeg = pctFromCoords(coords) * 360;
    for (const { slices, innerPct, outerPct, key } of RINGS) {
      if (distance < R * innerPct || distance > R * outerPct) continue;
      const total = slices.reduce((s, d) => s + (d.value || 0), 0);
      if (!total) return null;
      let acc = 0;
      for (const s of slices) {
        acc += (s.value / total) * 360;
        if (angleDeg <= acc) return s.label != null ? { key, label: s.label } : null;
      }
    }
    return null;
  };

  const isNearNeedle = (coords) => {
    if (pctRef.current == null) return false;
    const distance = Math.sqrt(coords.x * coords.x + coords.y * coords.y);
    if (distance < R * 0.15 || distance > R * 0.8) return false;
    const diff = Math.abs(pctFromCoords(coords) - pctRef.current) * 360;
    return Math.min(diff, 360 - diff) <= NEEDLE_GRAB_DEG;
  };

  // Seul un `touchstart` non passif peut empêcher le défilement, et seulement quand le
  // doigt part de l'aiguille ; ailleurs, le navigateur fait défiler la page normalement.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const onTouchStart = (e) => {
      const t = e.touches[0];
      if (t && isNearNeedle(getCoords(t))) e.preventDefault();
    };
    svg.addEventListener('touchstart', onTouchStart, { passive: false });
    return () => svg.removeEventListener('touchstart', onTouchStart);
  });

  const handlePointerDown = (e) => {
    if (e.pointerType === 'mouse') e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    pointerState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startT: Date.now(),
      moved: false,
      active: true,
      grabbed: e.pointerType === 'mouse' || isNearNeedle(getCoords(e)),
    };
  };

  // Le navigateur a repris le geste pour faire défiler : ce n'est ni un tap ni un glissé.
  const handlePointerCancel = () => {
    pointerState.current = { startX: 0, startY: 0, startT: 0, moved: false, active: false, grabbed: false };
    setDragging(false);
  };

  const handlePointerMove = (e) => {
    const ps = pointerState.current;
    if (!ps.active) return;
    const dx = e.clientX - ps.startX;
    const dy = e.clientY - ps.startY;
    if (!ps.moved && dx * dx + dy * dy > DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
      ps.moved = true;
      if (hasNeedle && ps.grabbed) setDragging(true);
    }
    if (ps.moved && hasNeedle && ps.grabbed) setLocalPct(pctFromCoords(getCoords(e)));
  };

  const handlePointerUp = (e) => {
    const ps = pointerState.current;
    if (!ps.active) { setDragging(false); return; }
    const wasTap = !ps.moved && Date.now() - ps.startT < TAP_MAX_MS;
    pointerState.current = { startX: 0, startY: 0, startT: 0, moved: false, active: false, grabbed: false };
    setDragging(false);
    if (wasTap && onSliceClick) {
      const hit = hitTestSlice(getCoords(e));
      if (hit) onSliceClick(hit.key, hit.label);
    }
  };

  return (
    // Plafonné : à 100 % de la carte, le donut montait à ~560 px sur ordinateur.
    <div style={{ opacity: isEstimated ? 0.7 : 1, width: '100%', maxWidth: 320, margin: '0 auto' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        style={{ display: 'block', cursor: hasNeedle ? (dragging ? 'grabbing' : 'grab') : 'default', touchAction: 'manipulation', userSelect: 'none', WebkitTapHighlightColor: 'transparent' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        {/* Donut rings */}
        {RINGS.map(({ slices, innerPct, outerPct, key }, ri) => {
          const total = slices.reduce((s, d) => s + (d.value || 0), 0);
          if (!total) return null;
          let angle = 0;
          return slices.map((slice, i) => {
            const sweep = (slice.value / total) * 360;
            const start = angle;
            angle += sweep;
            if (sweep < 0.1) return null;
            const isActive = activeSlice?.ri === ri && activeSlice?.i === i;
            const isOtherActive = activeSlice && !isActive;
            const popOut = isActive ? 0.02 : 0;
            return (
              <path
                key={`${ri}-${i}`}
                d={arcPath(cx, cy, R * (innerPct - popOut), R * (outerPct + popOut), start, start + sweep)}
                fill={slice.fill}
                stroke={isActive ? UI.white : 'transparent'}
                strokeWidth={isActive ? 1.5 : 0}
                opacity={isOtherActive ? 0.4 : 1}
                style={{ cursor: onSliceClick ? 'pointer' : 'default', transition: 'opacity 0.18s ease, stroke-width 0.18s ease', outline: 'none' }}
                onMouseEnter={() => setActiveSlice({ ri, i })}
                onMouseLeave={() => setActiveSlice(null)}
              />
            );
          });
        })}

        {/* Center: cycle day + phase */}
        {displayDay != null && (
          <g style={{ pointerEvents: 'none' }}>
            <text x={cx} y={phaseAtNeedle ? cy - 7 : cy} fontSize="20" fontWeight="800"
              fill={phaseAtNeedle?.color ?? UI.textPrimary}
              textAnchor="middle" dominantBaseline="middle" fontFamily="system-ui, sans-serif">
              J{displayDay}
            </text>
            {phaseAtNeedle && (
              <text x={cx} y={cy + 10} fontSize="8" fontWeight="600"
                fill={phaseAtNeedle.color} textAnchor="middle" dominantBaseline="middle"
                fontFamily="system-ui, sans-serif" opacity="0.75">
                {phaseAtNeedle.label}
              </text>
            )}
          </g>
        )}

        {/* Cycle day tick labels (every 3rd day) */}
        {Array.from({ length: Math.ceil(cycleDays / 3) }, (_, k) => {
          const day = 1 + k * 3;
          const angle = ((day - 1) / cycleDays * 360 - 90) * Math.PI / 180;
          const r = R * 0.78;
          const lx = cx + r * Math.cos(angle);
          const ly = cy + r * Math.sin(angle);
          return (
            <text key={day} x={lx.toFixed(1)} y={ly.toFixed(1)} fontSize="10" fill={HOME.axisRing}
              textAnchor="middle" dominantBaseline="middle" fontFamily="system-ui, sans-serif"
              style={{ pointerEvents: 'none' }}>
              {day}
            </text>
          );
        })}

        {/* Needle (rendered last → highest z-index in SVG) */}
        {activePct != null && (() => {
          const rad = ((activePct * 360 - 90) * Math.PI) / 180;
          // Dépasse symétriquement de 0.06·R de chaque côté de la bande d'anneaux (0.28 → 0.66)
          const rInner = R * 0.22;
          const rOuter = R * 0.72;
          const x1 = (cx + rInner * Math.cos(rad)).toFixed(2);
          const y1 = (cy + rInner * Math.sin(rad)).toFixed(2);
          const x2 = (cx + rOuter * Math.cos(rad)).toFixed(2);
          const y2 = (cy + rOuter * Math.sin(rad)).toFixed(2);
          return (
            <g style={{ pointerEvents: 'none' }}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} style={{ stroke: UI.surfaceEmpty, strokeWidth: 5.5, strokeLinecap: 'round' }} />
              <line x1={x1} y1={y1} x2={x2} y2={y2} style={{ stroke: UI.white, strokeWidth: 3.5, strokeLinecap: 'round' }} />
            </g>
          );
        })()}
      </svg>
    </div>
  );
};
const PHASE_CONFIG = {
  menstrual:  { label: 'Règles',       color: PHASE.regles,       bg: PHASE.regles       + '22', Icon: ReglesIcon },
  follicular: { label: 'Folliculaire', color: PHASE.folliculaire, bg: PHASE.folliculaire  + '66', Icon: CycleIcon },
  ovulation:  { label: 'Ovulation',    color: PHASE.ovulation,    bg: PHASE.ovulation    + '33', Icon: OvulationIcon },
  luteal:     { label: 'Lutéale',      color: PHASE.luteale,      bg: PHASE.luteale      + '55', Icon: CycleIcon },
};
const PHASE_TIPS = {
  menstrual: 'Repos & récupération recommandés 📊',
  follicular: "Idéal pour l'endurance et le cardio 🏃",
  ovulation: 'Performance sportive à son maximum ⚡',
  luteal: 'Meilleure construction musculaire 💪',
};

const PHASE_FR_MAP = {
  'Règles': 'menstrual', 'Folliculaire': 'follicular',
  'Ovulation': 'ovulation', 'Lutéale': 'luteal',
};

const PHASE_EMOJI = {
  'Règles': '🩸', 'Folliculaire': '🌱', 'Ovulation': '🥚', 'Lutéale': '🌙',
};

const QUICK_ADD = [
  { key: 'cycle',  label: 'Ajouter mon cycle',  Icon: ReglesIcon },
  { key: 'mental', label: 'Ajouter mon mental', Icon: MentalIcon },
  { key: 'sport',  label: 'Ajouter mon sport',  Icon: ActivityIcon },
];

const Home = () => {
  const navigate = useNavigate();
  const { user, getToken } = useAuth();

  const [cycles, setCycles] = useState([]);
  const [mentals, setMentals] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [respondingId, setRespondingId] = useState(null);

  const [mentalRangeDays, setMentalRangeDays] = useState(() => {
    const v = Number(window.localStorage.getItem('period:home:mental'));
    return Number.isFinite(v) && v > 0 ? v : 30;
  });
  const [sportRangeDays, setSportRangeDays] = useState(() => {
    const v = Number(window.localStorage.getItem('period:home:sport'));
    return Number.isFinite(v) && v > 0 ? v : 30;
  });
  // Période affichée : 0 = celle qui finit aujourd'hui, 1 = la précédente… (cf. PeriodPager).
  const [mentalOffset, setMentalOffset] = usePeriodOffset(mentalRangeDays);
  const [sportOffset, setSportOffset] = usePeriodOffset(sportRangeDays);
  const loadMainData = useCallback(() => {
    if (!user?.id) { setLoading(false); return; }
    const token = getToken();
    setLoading(true);
    setError(null);
    Promise.all([
      apiGetCycles(user.id, token),
      apiGetMentals(user.id, token),
      apiGetSports(user.id, token),
    ]).then(([c, m, s]) => {
      setCycles(c);
      setMentals(m);
      setSports(s);
    }).catch(() => setError('Impossible de charger tes données. Vérifie ta connexion et réessaie.'))
      .finally(() => setLoading(false));
  }, [user?.id, getToken]);

  useEffect(() => {
    loadMainData();
  }, [loadMainData]);

  // Série de jours de saisie. Rechargée à chaque retour sur l'accueil, donc juste
  // après une saisie : le formulaire ramène ici.
  const [streak, setStreak] = useState(null);
  useEffect(() => {
    if (!user?.id) return;
    apiGetStreak(getToken()).then(setStreak).catch(() => {});
  }, [user?.id, getToken]);

  useEffect(() => {
    if (!user?.id) return;
    apiGetDashboardSummary(user.id, getToken())
      .then((d) => {
        setDashboardData(d);
        // Mémorise la phase pour que la carte « Astuce du jour » s'affiche dès le
        // premier rendu à la prochaine visite, sans attendre ce résumé. Traduction
        // obligatoire — l'API renvoie un libellé français, les astuces s'indexent sur
        // les clés du moteur.
        rememberPhase(PHASE_KEY_FROM_LABEL[d?.current_phase]);
      })
      .catch(() => {});
  }, [user?.id, getToken]);

  useEffect(() => {
    if (!user?.id) return;
    apiGetPendingInvitations(user.id, getToken())
      .then(setInvitations)
      .catch(() => {});
  }, [user?.id, getToken]);

  const respondToInvitation = async (invitationId, accept) => {
    setRespondingId(invitationId);
    try {
      await apiRespondToInvitation(invitationId, accept, getToken());
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    } catch {
      // L'invitation reste affichée si la réponse échoue ; la sportive peut réessayer.
    } finally {
      setRespondingId(null);
    }
  };

  const mentalsByDateAsc = useMemo(
    () => [...mentals].sort((a, b) => String(a.date).localeCompare(String(b.date))),
    [mentals]
  );
  const sportsByDateAsc = useMemo(
    () => [...sports].sort((a, b) => String(a.date).localeCompare(String(b.date))),
    [sports]
  );
  const mentalsRange = useMemo(
    () => filterWindow(mentalsByDateAsc, periodWindow(mentalRangeDays, mentalOffset), (m) => m.date),
    [mentalsByDateAsc, mentalRangeDays, mentalOffset]
  );
  const sportWindow = useMemo(() => periodWindow(sportRangeDays, sportOffset), [sportRangeDays, sportOffset]);
  const sportsRange = useMemo(
    () => filterWindow(sportsByDateAsc, sportWindow, (s) => s.date),
    [sportsByDateAsc, sportWindow]
  );
  const today = todayISO();
  const findEntry = (list) => list.find(e => e.date === today) ?? list.at(-1) ?? null;
  const hasTodayData =
    cycles.some(e => e.date === today) ||
    mentals.some(e => e.date === today) ||
    sports.some(e => e.date === today);

  const cycleEntry = findEntry(cycles);
  const mentalEntry = findEntry(mentals);
  const sportEntry = findEntry(sports);

  const moodEmoji = mentalEntry?.mood ? (MOOD_EMOJI[mentalEntry.mood] ?? '😊') : null;
  const moodLabel = mentalEntry?.mood ?? null;
  const rpeValue = sportEntry?.rpe != null ? sportEntry.rpe : null;

  // La phase vient du backend, qui l'ancre sur les règles (déclarées à
  // l'inscription ou détectées dans les relevés). Le relevé du jour ne sert que
  // de repli : lui seul ne dit rien de la position dans le cycle — d'où le
  // « Folliculaire » par défaut qu'affichait cet écran.
  let phaseLabel = dashboardData?.current_phase ?? null;
  if (!phaseLabel && cycleEntry) {
    if (cycleEntry.ovulation_test) phaseLabel = 'Ovulation';
    else if (cycleEntry.flow) phaseLabel = 'Règles';
  }

  // Couleurs dynamiques des puces du haut : phase courante + RPE du jour
  const bubblePhaseKey = PHASE_FR_MAP[phaseLabel] ?? null;
  const bubblePhaseColor = bubblePhaseKey ? PHASE_CONFIG[bubblePhaseKey]?.color : null;
  const rpeBubbleColor = rpeValue != null ? (rpeValue <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense) : null;

  // Mental averages from selected period window
  const avg = (field) => {
    const vals = mentalsRange.map(m => m[field]).filter(v => v != null);
    if (!vals.length) return null;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10);
  };
  const concentrationPct = avg('concentration');
  const selfEsteemPct = avg('self_esteem');
  const productivityPct = avg('productivity');

  // Sport stats — RPE + HR both use sportsRange
  const rpeEntries = sportsRange.filter(s => s.rpe != null);
  const avgRpe = rpeEntries.length
    ? (rpeEntries.reduce((a, s) => a + s.rpe, 0) / rpeEntries.length).toFixed(1)
    : null;
  const avgRpeColor = avgRpe != null ? (parseFloat(avgRpe) <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense) : null;
  const restHREntries = sportsRange.filter(s => s.rest_heart_rate != null);
  const avgRestHR = restHREntries.length
    ? Math.round(restHREntries.reduce((a, s) => a + s.rest_heart_rate, 0) / restHREntries.length)
    : null;
  const effortHREntries = sportsRange.filter(s => s.effort_heart_rate != null);
  const avgEffortHR = effortHREntries.length
    ? Math.round(effortHREntries.reduce((a, s) => a + s.effort_heart_rate, 0) / effortHREntries.length)
    : null;

  // Chart data: one slot per day for the full range (null for missing days)
  const sportChartData = useMemo(() => {
    const byDate = {};
    sportsRange.forEach(s => { byDate[String(s.date)] = s; });
    const result = [];
    windowDays(sportWindow).forEach((d) => {
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const entry = byDate[dateStr];
      result.push({
        dateStr,
        dayLabel: ['D', 'L', 'M', 'M', 'J', 'V', 'S'][d.getDay()],
        rpe: entry?.rpe ?? null,
        restHR: entry?.rest_heart_rate ?? null,
        effortHR: entry?.effort_heart_rate ?? null,
      });
    });
    return result;
  }, [sportsRange, sportWindow]);

  const [showReliabilityModal, setShowReliabilityModal] = useState(false);
  const [ringModal, setRingModal] = useState(null); // null | { ring: string, sliceLabel: string | null }
  const ringModalOpenedAt = useRef(0);
  const openRingModal = (m) => { ringModalOpenedAt.current = Date.now(); setRingModal(m); };
  const guardModalClick = (fn) => () => {
    if (Date.now() - ringModalOpenedAt.current < 400) return;
    fn();
  };

  const RING_META = {
    cycle:  { title: 'Cycle menstruel',   label: 'le cycle' },
    mental: { title: 'État mental',       label: 'le mental' },
    sport:  { title: 'Activité sportive', label: 'le sport' },
  };

  const RingModalContent = ({ ring, data, selectedSlice }) => {
    if (!data) return <p style={{ color: UI.textSecondary, fontSize: '0.85rem', margin: '8px 0' }}>Pas encore de données.</p>;

    // Les trois anneaux partagent une même fenêtre, choisie par le serveur : le dernier
    // cycle complet dès que deux J1 sont connus, 30 jours seulement à défaut. L'écran
    // annonçait « 30 j » en dur, ce qui était faux pour la plupart des comptes.
    const windowLabel = (() => {
      if (!data.pie_start || !data.pie_end) return '30 j';
      const fmt = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
      return `${fmt(data.pie_start)} – ${fmt(data.pie_end)}`;
    })();

    const sliceRow = (s, i) => {
      const isSelected = selectedSlice && s.label === selectedSlice;
      return (
        <div key={i} className="home__ring-modal-row" style={isSelected ? { background: s.color + '22', borderRadius: '8px', margin: '0 -6px', padding: '4px 6px' } : {}}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0, display: 'inline-block' }} />
          <span style={{ flex: 1, color: UI.textPrimary, fontWeight: isSelected ? 700 : 400 }}>{s.label}</span>
          <span style={{ fontWeight: 700, color: s.color }}>{Math.round(s.pct)}%</span>
        </div>
      );
    };

    if (ring === 'cycle') {
      const phase = PHASE_FR_MAP[data.current_phase];
      const cfg = phase ? PHASE_CONFIG[phase] : null;
      return (
        <div>
          {cfg && (
            <div style={{ display: 'inline-block', background: cfg.bg, color: cfg.color, borderRadius: '20px', padding: '4px 14px', fontSize: '0.8rem', fontWeight: 700, marginBottom: '12px' }}>
              {cfg.label}
            </div>
          )}
          <p style={{ fontSize: '0.75rem', color: UI.textSecondary, margin: '0 0 10px' }}>Répartition du cycle ({windowLabel})</p>
          {(data.cycle_pie || []).map(sliceRow)}
          {data.is_estimated && <p style={{ fontSize: '0.75rem', color: UI.textSecondary, marginTop: '8px', fontStyle: 'italic' }}>Estimation — s'affine avec un suivi régulier</p>}
        </div>
      );
    }

    if (ring === 'mental') {
      const mood = data.current_mood;
      const emoji = mood ? (MOOD_EMOJI[mood] ?? '😊') : null;
      return (
        <div>
          {mood && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '1.8rem' }}>{emoji}</span>
              <span style={{ fontSize: '0.95rem', fontWeight: 600, color: UI.textPrimary }}>{mood}</span>
            </div>
          )}
          <p style={{ fontSize: '0.75rem', color: UI.textSecondary, margin: '0 0 10px' }}>Répartition de l'humeur ({windowLabel})</p>
          {(data.mental_pie || []).map(sliceRow)}
        </div>
      );
    }

    if (ring === 'sport') {
      const rpe = data.current_rpe;
      const rpeColor = rpe != null ? (rpe <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense) : null;
      return (
        <div>
          {rpe != null && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '12px' }}>
              <span style={{ fontSize: '2rem', fontWeight: 800, color: rpeColor }}>{rpe}</span>
              <span style={{ fontSize: '0.9rem', color: UI.textSecondary }}>/10 RPE</span>
            </div>
          )}
          <p style={{ fontSize: '0.75rem', color: UI.textSecondary, margin: '0 0 10px' }}>Répartition de l'effort ({windowLabel})</p>
          {(data.sport_pie || []).map(sliceRow)}
        </div>
      );
    }

    return null;
  };

  const reliabilityPct = dashboardData?.reliability_pct ?? 0;
  const todayPhaseKey = PHASE_FR_MAP[dashboardData?.current_phase] ?? null;
  const todayPhaseConfig = todayPhaseKey ? PHASE_CONFIG[todayPhaseKey] : null;
  const currentPhase = todayPhaseKey;

  // Tant que le résumé n'est pas arrivé, la dernière phase mémorisée prend le relais :
  // la carte est là dès le premier rendu au lieu d'apparaître après coup et de
  // pousser le camembert vers le bas.
  const tip = tipOfTheDay(PHASE_KEY_FROM_LABEL[dashboardData?.current_phase] ?? recallPhase());

  return (
    <div className="home">
      <PageHeader title="Accueil" />

      {error && <ErrorBanner message={error} onRetry={loadMainData} />}

      {/* Widget 1: Greeting */}
      <div className="home__greeting">
        <div className="home__greeting-text">
          <h2>Hello <span className="home__name">{user?.prenom || 'Utilisateur'}</span>,</h2>
          {!hasTodayData && <p>N'oublies pas d'<strong style={{ color: 'var(--mental-primary)' }}>inscrire tes données</strong> aujourd'hui !</p>}
          {hasTodayData && currentPhase && (
            <p className="home__greeting-tip">{PHASE_TIPS[currentPhase]}</p>
          )}
        </div>
        <div className="home__greeting-actions">
          <button className="home__greeting-btn home__greeting-btn--form" onClick={() => navigate('/formulaire/cycle')}>
            <FormIcon className="home__greeting-icon" />
          </button>
          <button className="home__greeting-btn home__greeting-btn--share" onClick={() => navigate('/formulaire/share')}>
            <ShareIcon className="home__greeting-icon" />
          </button>
        </div>
      </div>

      <StreakBanner streak={streak} today={todayISO()} />

      {/* CTA de saisie quotidienne.
          La seule porte d'entrée vers le questionnaire était l'icône 40×40
          ci-dessus, sans libellé : les bêta-testeuses mettaient un temps fou à
          la trouver. Ce bouton pleine largeur nomme explicitement l'action. */}
      <button
        className={`home__daily-cta${hasTodayData ? ' home__daily-cta--done' : ''}`}
        onClick={() => navigate('/formulaire/cycle')}
      >
        <span className="home__daily-cta-icon" aria-hidden="true">{hasTodayData ? '✓' : '+'}</span>
        <span className="home__daily-cta-text">
          <span className="home__daily-cta-title">
            {hasTodayData ? 'Modifier mes données du jour' : 'Saisir mes données du jour'}
          </span>
          <span className="home__daily-cta-sub">
            {hasTodayData
              ? 'Déjà enregistrées — tu peux les compléter'
              : 'Cycle, mental et sport en 2 minutes'}
          </span>
        </span>
        <span className="home__daily-cta-arrow" aria-hidden="true">›</span>
      </button>

      {/* Saisie d'un seul volet : `solo` ramène ici après l'enregistrement, là où le
          bouton ci-dessus déroule les trois formulaires à la suite. */}
      <div className="home__quick-add">
        {QUICK_ADD.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`home__quick-add-btn home__quick-add-btn--${key}`}
            onClick={() => navigate(`/formulaire/${key}`, { state: { solo: true } })}
          >
            <Icon className="home__quick-add-icon" aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>


      {/* Widget 1bis: Notification de demande de suivi coach */}
      {invitations.length > 0 && (
        <div className="home__notification">
          <div className="home__notification-icon">
            <ShareIcon className="home__notification-icon-svg" />
          </div>
          <div className="home__notification-text">
            <p className="home__notification-title">
              <strong>{invitations[0].coach_prenom} {invitations[0].coach_nom}</strong> souhaite te suivre
            </p>
            <p className="home__notification-sub">Acceptes-tu de partager tes données avec ce coach ?</p>
          </div>
          <div className="home__notification-actions">
            <button
              className="home__notification-btn home__notification-btn--no"
              disabled={respondingId === invitations[0].id}
              onClick={() => respondToInvitation(invitations[0].id, false)}
            >
              Non
            </button>
            <button
              className="home__notification-btn home__notification-btn--yes"
              disabled={respondingId === invitations[0].id}
              onClick={() => respondToInvitation(invitations[0].id, true)}
            >
              Oui
            </button>
          </div>
        </div>
      )}

      {/* Widget 2: Quick info bubbles */}
      <div className="home__bubbles">
        <div className="home__bubble home__bubble--cycle" onClick={() => navigate('/cycle')} style={{ cursor: 'pointer', ...(bubblePhaseColor ? { '--bubble-accent': bubblePhaseColor } : {}) }}>
          <span className="home__bubble-label">Phase</span>
          <span className="home__bubble-value" style={{ fontSize: phaseLabel ? '0.9rem' : '0.75rem' }}>
            {loading ? '…' : (phaseLabel ? (PHASE_EMOJI[phaseLabel] ?? '🌱') : '—')}
          </span>
          <span className="home__bubble-sub">{loading ? '' : (phaseLabel ?? '')}</span>
        </div>
        <div className="home__bubble home__bubble--mood" onClick={() => navigate('/mental')} style={{ cursor: 'pointer' }}>
          <span className="home__bubble-label">Je suis...</span>
          <span className="home__bubble-value">{loading ? '…' : (moodEmoji ?? '—')}</span>
          <span className="home__bubble-sub">{loading ? '' : (moodLabel ?? '')}</span>
        </div>
        <div
          className="home__bubble home__bubble--feeling"
          onClick={() => navigate('/sport')}
          style={{ cursor: 'pointer', ...(rpeBubbleColor ? { '--bubble-accent': rpeBubbleColor } : {}) }}
        >
          <span className="home__bubble-label">Ressenti</span>
          <span className="home__bubble-value">
            {loading ? '…' : (rpeValue != null ? <>{rpeValue}<span className="home__bubble-fraction">/10</span></> : '—')}
          </span>
          <span className="home__bubble-sub">{rpeValue != null ? "de l'effort" : ''}</span>
        </div>
      </div>

      {/* Widget 2bis: Astuce du jour — même forme que la notification coach */}
      <div
        className="home__notification home__notification--tip"
        style={todayPhaseConfig ? { '--tip-accent': todayPhaseConfig.color } : undefined}
      >
        <div className="home__notification-icon" aria-hidden="true">💡</div>
        <div className="home__notification-text">
          <p className="home__tip-eyebrow">
            Astuce du jour{todayPhaseConfig ? ` · ${todayPhaseConfig.label}` : ''}
          </p>
          <p className="home__notification-title"><strong>{tip.titre}</strong></p>
          <p className="home__notification-sub">{tip.corps}</p>
        </div>
      </div>

      {/* Widget 3: 3 pie charts (cycle / mental / sport) */}
      <div className="home__widget home__chart-widget">
        <div className="home__circle-header">
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: UI.textPrimary }}>Répartition du mois</span>
          <button className="home__circle-reliability" onClick={() => setShowReliabilityModal(true)}>
            {reliabilityPct}%&nbsp;<InfoFillIcon className="home__circle-reliability-icon" />
          </button>
        </div>
        <div className="home__pies">
          <ConcentricPie
            cycleSlices={dashboardData?.cycle_pie?.map(s => ({ ...s, color: CYCLE_PIE_COLORS[s.label] ?? s.color }))}
            cycleSlicesRaw={dashboardData?.cycle_pie?.map(s => ({ ...s, color: CYCLE_PIE_COLORS[s.label] ?? s.color }))}
            mentalSlices={dashboardData?.mental_pie?.map(s => ({ ...s, color: MENTAL_PIE_COLORS[s.label] ?? s.color }))}
            sportSlices={dashboardData?.sport_pie?.map(s => ({ ...s, color: SPORT_PIE_COLORS[s.label] ?? s.color }))}
            cycleDayPct={dashboardData?.cycle_day_pct ?? null}
            cycleDay={dashboardData?.cycle_day ?? null}
            cycleDays={dashboardData?.cycle_length || 28}
            isEstimated={!!dashboardData?.is_estimated}
            size={290}
            onSliceClick={(ring, sliceLabel) => openRingModal({ ring, sliceLabel })}
          />
        </div>
        <div className="home__pie-legend">
          <span className="home__pie-legend-item"><span className="home__pie-legend-dot" style={{ background: PHASE.ovulation }} />Cycle<InfoButton infoKey="concentricCycle" /></span>
          <span className="home__pie-legend-item"><span className="home__pie-legend-dot" style={{ background: MENTAL.concentration }} />Mental<InfoButton infoKey="concentricMental" /></span>
          <span className="home__pie-legend-item"><span className="home__pie-legend-dot" style={{ background: SPORT.fcEffort }} />Sport<InfoButton infoKey="concentricSport" /></span>
        </div>
        {dashboardData?.is_estimated && (
          <p className="home__pies-estimated">Estimation — s'affine avec un suivi régulier</p>
        )}
      </div>

      {/* Widget 4: Menstrual cycle summary */}
      <div className="home__widget home__widget--cycle">
        <div className="home__widget-header">
          <div className="home__widget-icon home__widget-icon--cycle" style={{ background: 'color-mix(in srgb, var(--phase-ovulation) 18%, transparent)', color: 'var(--phase-ovulation)' }}>
            {todayPhaseConfig ? <todayPhaseConfig.Icon /> : <OvulationIcon />}
          </div>
          <div className="home__widget-title-group">
            <h3>Cycle menstruel</h3>
            <p className="home__widget-subtitle" style={todayPhaseConfig ? { color: todayPhaseConfig.color } : {}}>
              {loading ? '…' : (todayPhaseConfig?.label ?? phaseLabel ?? 'Aucune donnée')}
            </p>
          </div>
        </div>
        {cycles.length === 0 && !loading ? (
          <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0' }}>Aucune donnée de cycle enregistrée</p>
        ) : (
          <div className="home__cycle-week">
            <div className="home__cycle-days">
              <span>L</span><span>M</span><span>M</span><span>J</span>
              <span>V</span><span>S</span><span>D</span>
            </div>
            <div className="home__cycle-numbers">
              {Array.from({ length: 7 }, (_, i) => {
                const d = new Date();
                d.setDate(d.getDate() - 6 + i);
                const dateStr = toISODate(d);
                const entry = cycles.find(c => c.date === dateStr);
                const dayNum = d.getDate();
                const isToday = dateStr === today;
                const dotColor = entry?.flow ? 'var(--phase-menstruations)'
                  : entry?.ovulation_test ? 'var(--phase-ovulation)'
                  : entry && !entry.is_rest_day ? 'var(--phase-folliculaire)'
                  : null;
                return (
                  <div key={i} className="home__cycle-day">
                    <span className={`home__cycle-num${isToday ? ' home__cycle-num--active' : ''}`}>{dayNum}</span>
                    {dotColor && <span className="home__cycle-dot" style={{ background: dotColor }} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Widget 5: Mental summary */}
      <div className="home__widget home__widget--mental">
        <div className="home__widget-header">
          <div className="home__widget-icon home__widget-icon--mental">
            <MentalIcon />
          </div>
          <div className="home__widget-title-group">
            <h3>Mental<InfoButton infoKey="recapMental" /></h3>
          </div>
          <PeriodSelector
            value={mentalRangeDays}
            onChange={setMentalRangeDays}
            options={[30, 15, 7]}
            storageKey="period:home:mental"
            className="home__widget-badge"
            arrowClassName="home__badge-arrow"
          />
        </div>
        <PeriodPager days={mentalRangeDays} offset={mentalOffset} onOffsetChange={setMentalOffset} earliest={mentalsByDateAsc[0]?.date}>
          {(() => {
          return (
          <div className="home__mental-stats">
            {/* Energy */}
            <div className="home__mental-stat">
              <div className="home__mental-stat-icon home__mental-stat-icon--energy" style={{ position: 'relative', width: '32px', height: '32px' }}>
                <div style={{ position: 'absolute', inset: 0, opacity: 0.2 }}>
                  <EnergieIcon style={{ width: '100%', height: '100%' }} />
                </div>
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                  <defs>
                    <clipPath id="energyClip">
                      <rect x="0" y={`${100 - (productivityPct ?? 0)}%`} width="100%" height={`${productivityPct ?? 0}%`} />
                    </clipPath>
                  </defs>
                  <foreignObject x="0" y="0" width="100%" height="100%" clipPath="url(#energyClip)">
                    <EnergieIcon style={{ width: '32px', height: '32px' }} />
                  </foreignObject>
                </svg>
              </div>
              <span className="home__mental-stat-value home__mental-stat-value--energy">
                {productivityPct != null ? `${productivityPct}%` : '—'}
              </span>
              <span className="home__mental-stat-label">Productivité</span>
            </div>
            {/* Concentration */}
            <div className="home__mental-stat">
              <div className="home__mental-stat-icon home__mental-stat-icon--focus" style={{ position: 'relative', width: '32px', height: '32px' }}>
                <div style={{ position: 'absolute', inset: 0, opacity: 0.2 }}>
                  <ConcentrationIcon style={{ width: '100%', height: '100%' }} />
                </div>
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                  <defs>
                    <clipPath id="focusClip">
                      <rect x="0" y={`${100 - (concentrationPct ?? 0)}%`} width="100%" height={`${concentrationPct ?? 0}%`} />
                    </clipPath>
                  </defs>
                  <foreignObject x="0" y="0" width="100%" height="100%" clipPath="url(#focusClip)">
                    <ConcentrationIcon style={{ width: '32px', height: '32px' }} />
                  </foreignObject>
                </svg>
              </div>
              <span className="home__mental-stat-value home__mental-stat-value--focus">
                {concentrationPct != null ? `${concentrationPct}%` : '—'}
              </span>
              <span className="home__mental-stat-label">Concentration</span>
            </div>
            {/* Self-esteem */}
            <div className="home__mental-stat">
              <div className="home__mental-stat-icon home__mental-stat-icon--esteem" style={{ position: 'relative', width: '32px', height: '32px' }}>
                <div style={{ position: 'absolute', inset: 0, opacity: 0.2 }}>
                  <EstimeIcon style={{ width: '100%', height: '100%' }} />
                </div>
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                  <defs>
                    <clipPath id="esteemClip">
                      <rect x="0" y={`${100 - (selfEsteemPct ?? 0)}%`} width="100%" height={`${selfEsteemPct ?? 0}%`} />
                    </clipPath>
                  </defs>
                  <foreignObject x="0" y="0" width="100%" height="100%" clipPath="url(#esteemClip)">
                    <EstimeIcon style={{ width: '32px', height: '32px' }} />
                  </foreignObject>
                </svg>
              </div>
              <span className="home__mental-stat-value home__mental-stat-value--esteem">
                {selfEsteemPct != null ? `${selfEsteemPct}%` : '—'}
              </span>
              <span className="home__mental-stat-label">Estime de soi</span>
            </div>
          </div>
          );
          })()}
        </PeriodPager>
      </div>

      {/* Widget 6: Sport activity summary */}
      <div className="home__widget home__widget--sport">
        <div className="home__widget-header">
          <div className="home__widget-icon home__widget-icon--sport">
            <ActivityIcon />
          </div>
          <div className="home__widget-title-group">
            <h3>Activité sportive<InfoButton infoKey="activiteSportive" /></h3>
          </div>
          <PeriodSelector
            value={sportRangeDays}
            onChange={setSportRangeDays}
            options={[30, 15, 7]}
            storageKey="period:home:sport"
            className="home__widget-badge"
            arrowClassName="home__badge-arrow"
          />
        </div>
        <PeriodPager days={sportRangeDays} offset={sportOffset} onOffsetChange={setSportOffset} earliest={sportsByDateAsc[0]?.date}>
          {sportChartData.length === 0 && !loading ? (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0', fontSize: '0.85rem' }}>Aucune donnée sportive pour cette période</p>
          ) : (<>
          {(() => {
            const PLOT_X0 = 34;
            const PLOT_X1 = 292;
            const PLOT_Y_TOP = 8;
            const PLOT_Y_BOT = 152;
            const plotW = PLOT_X1 - PLOT_X0;
            const plotH = PLOT_Y_BOT - PLOT_Y_TOP;
            const n = sportChartData.length;
            const toX = (i) => PLOT_X0 + (i / Math.max(n - 1, 1)) * plotW;
            const toY = (pct) => PLOT_Y_BOT - pct * plotH;
            const BAR_W = Math.max(4, Math.min(20, Math.floor(plotW / Math.max(n, 1)) - 2));

            const restPts = sportChartData
              .map((d, i) => d.restHR != null ? `${toX(i)},${toY(d.restHR / 200)}` : null)
              .filter(Boolean).join(' ');
            const effortPts = sportChartData
              .map((d, i) => d.effortHR != null ? `${toX(i)},${toY(d.effortHR / 200)}` : null)
              .filter(Boolean).join(' ');

            const yTicks = [{ pct: 1, label: '200' }, { pct: 0.5, label: '100' }, { pct: 0.25, label: '50' }, { pct: 0, label: '0' }];

            const cycleByDate = Object.fromEntries(cycles.map(c => [String(c.date), c]));

            return (
              <svg width="100%" height="182" viewBox="0 0 300 182" style={{ display: 'block', padding: '0 4px' }}>
                {/* Grid lines + Y labels */}
                {yTicks.map(({ pct, label }) => (
                  <g key={label}>
                    <text x={PLOT_X0 - 4} y={toY(pct) + 4} fontSize="9" fill={UI.textAxis} textAnchor="end">{label}</text>
                    <line x1={PLOT_X0} y1={toY(pct)} x2={PLOT_X1} y2={toY(pct)} stroke="var(--color-grid)" strokeWidth="1" strokeDasharray={pct > 0 ? '4 4' : '0'} />
                  </g>
                ))}

                {/* RPE bars (green if ≤5, blue if >5) */}
                {sportChartData.map((d, i) => {
                  if (d.rpe == null) return null;
                  const pct = d.rpe / 10;
                  const barH = pct * plotH;
                  const barColor = d.rpe <= 5 ? 'var(--sport-rpe-facile)' : 'var(--sport-rpe-intense)';
                  return (
                    <rect key={i} x={toX(i) - BAR_W / 2} y={toY(pct)} width={BAR_W} height={barH} rx="4" ry="4" fill={barColor} opacity="0.85" />
                  );
                })}

                {/* Rest HR curve */}
                {restPts && <polyline points={restPts} fill="none" stroke="var(--sport-repos)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}

                {/* Effort HR curve */}
                {effortPts && <polyline points={effortPts} fill="none" stroke="var(--sport)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}

                {/* X labels + phase dots */}
                {sportChartData.map((d, i) => {
                  const step = n <= 7 ? 1 : n <= 15 ? 2 : 3;
                  const cyc = cycleByDate[d.dateStr];
                  const phaseColor = cyc ? (cyc.flow ? 'var(--cycle-primary)' : cyc.ovulation_test ? 'var(--sport-primary)' : 'var(--cycle-folliculaire)') : UI.surfaceEmpty;
                  return (
                    <g key={i}>
                      {(i % step === 0 || i === n - 1) && (
                        <text x={toX(i)} y="168" fontSize="10" fill={UI.textAxis} textAnchor="middle">{d.dayLabel}</text>
                      )}
                      <circle cx={toX(i)} cy="178" r="4" fill={phaseColor} />
                    </g>
                  );
                })}
              </svg>
            );
          })()}
          <div className="home__sport-stats">
            <div className="home__sport-stat home__sport-stat--yellow">
              <span className="home__sport-stat-label">FC repos</span>
              <span className="home__sport-stat-value home__sport-stat-value--yellow">
                {avgRestHR != null ? `${avgRestHR} bpm` : '—'}
              </span>
            </div>
            <div className="home__sport-stat home__sport-stat--red">
              <span className="home__sport-stat-label">FC à l'effort</span>
              <span className="home__sport-stat-value home__sport-stat-value--red">
                {avgEffortHR != null ? `${avgEffortHR} bpm` : '—'}
              </span>
            </div>
            <div className="home__sport-stat home__sport-stat--blue" style={avgRpeColor ? { background: avgRpeColor + '1A' } : undefined}>
              <span className="home__sport-stat-label">RPE moy</span>
              <span className="home__sport-stat-value home__sport-stat-value--blue" style={avgRpeColor ? { color: avgRpeColor } : undefined}>
                {avgRpe != null ? `${avgRpe}/10` : '—'}
              </span>
            </div>
          </div>
          </>)}
        </PeriodPager>
      </div>
      {/* Ring detail modals */}
      {ringModal && (
        <div
          className="home__modal-overlay"
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            if (Date.now() - ringModalOpenedAt.current < 400) return;
            setRingModal(null);
          }}
        >
          <div className="home__modal" onClick={e => e.stopPropagation()}>
            <div className="home__modal-header">
              <h3 className="home__modal-title">{RING_META[ringModal.ring].title}</h3>
              <button className="home__modal-close" onClick={guardModalClick(() => setRingModal(null))} aria-label="Fermer">✕</button>
            </div>
            <RingModalContent ring={ringModal.ring} selectedSlice={ringModal.sliceLabel} data={dashboardData ? {
              ...dashboardData,
              cycle_pie:  dashboardData.cycle_pie?.map(s => ({ ...s, color: CYCLE_PIE_COLORS[s.label] ?? s.color })),
              mental_pie: dashboardData.mental_pie?.map(s => ({ ...s, color: MENTAL_PIE_COLORS[s.label] ?? s.color })),
              sport_pie:  dashboardData.sport_pie?.map(s => ({ ...s, color: SPORT_PIE_COLORS[s.label] ?? s.color })),
            } : null} />
            <button
              className="home__ring-modal-btn"
              onClick={guardModalClick(() => { setRingModal(null); navigate(`/${ringModal.ring}`); })}
            >
              Voir {RING_META[ringModal.ring].label} →
            </button>
          </div>
        </div>
      )}

      {/* Reliability modal */}
      {showReliabilityModal && (
        <div className="home__modal-overlay" onClick={() => setShowReliabilityModal(false)}>
          <div className="home__modal" onClick={e => e.stopPropagation()}>
            <div className="home__modal-header">
              <h3 className="home__modal-title">Taux de fiabilité</h3>
              <button className="home__modal-close" onClick={() => setShowReliabilityModal(false)} aria-label="Fermer">✕</button>
            </div>
            <p className="home__modal-text">
              Ce pourcentage de {dashboardData?.reliability_pct ?? reliabilityPct}% représente la fiabilité des prédictions
              affichées, calculée en fonction des données que vous avez complétées.
              Plus vous renseignez vos informations quotidiennes, plus ce taux sera élevé
              et plus les prédictions seront précises.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
